import React, { useState, useEffect, useMemo } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import { requireListingImage } from '../../util/configHelpers';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';
import { trackRedditViewContentForListing } from '../../util/redditPixelEvents';
import { trackMetaViewContentForListing } from '../../util/metaPixelEvents';
import { pushRecentlyViewedListing } from '../../util/recentlyViewedListings';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  H4,
  H3,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';
import {
  addToCart,
  setCartPromoCode,
  addCartPromoCode,
  addBundleCartPromoCode,
} from '../../ducks/cart.duck';
import { buildCartOrderDataForListing } from '../../util/cartOrderDataFromListing';
import SectionBundles from './SectionBundles';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
  priceForSchemaMaybe,
} from './ListingPage.shared';
import SectionHero from './SectionHero';
import SectionTextMaybe from './SectionTextMaybe';
import SectionDescriptionAndDetails from './SectionDescriptionAndDetails';
import SectionMapMaybe from './SectionMapMaybe';
import ActionBarMaybe from './ActionBarMaybe';
import CompatibilityBadges from './CompatibilityBadges';
import SellerInfoPanel from './SellerInfoPanel';
import LoyaltyProgramPanel from './LoyaltyProgramPanel';
import { CartSidePanel } from '../../components';
import {
  isApril2026CampaignActive,
  isRoboupLawnMowerListing,
  isUserSignupApril2026,
} from '../../util/promoPrintempsRoboupClient';
import {
  isReolinkSecurityCameraListing,
  persistReolinkPromoUiSession,
  REOLINK_PROMO_CODE,
  shouldShowReolinkPromoPrice,
} from '../../util/promoReolinkWeekendClient';

import css from './ListingPage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;
const ROBOUP_PROMO_CODE = 'PRINTEMPSROBOUP';

const { UUID, Money } = sdkTypes;

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [imageCarouselOpen, setImageCarouselOpen] = useState(false);
  const [cartPanelOpen, setCartPanelOpen] = useState(false);
  const [sellerPanelOpen, setSellerPanelOpen] = useState(false);
  const [loyaltyPanelOpen, setLoyaltyPanelOpen] = useState(false);
  const [addingBundleId, setAddingBundleId] = useState(null);

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    showListingInProgress,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    ...restOfProps
  } = props;

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const listingUuidMaybe = currentListing?.id?.uuid;

  useEffect(() => {
    if (new URLSearchParams(location.search || '').get('promoReolink') === '1') {
      persistReolinkPromoUiSession();
    }
  }, [location.search]);

  useEffect(() => {
    if (!listingUuidMaybe || !currentListing?.id) {
      return;
    }
    trackRedditViewContentForListing(currentListing);
    trackMetaViewContentForListing(currentListing);
    pushRecentlyViewedListing(currentListing);
  }, [listingUuidMaybe]);

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'information';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  /* Promo + order-panel price memos must run before any early return (Rules of Hooks). */
  const publicDataForPromo =
    currentListing?.attributes?.publicData &&
    typeof currentListing.attributes.publicData === 'object'
      ? currentListing.attributes.publicData
      : {};
  const hasPromoSignupFlagEarly =
    new URLSearchParams(location.search || '').get('promoRoboup') === '1';
  const hasPromoReolinkFlagEarly =
    new URLSearchParams(location.search || '').get('promoReolink') === '1';
  const isRoboupPromoEligibleAfterSignupEarly =
    hasPromoSignupFlagEarly &&
    !!currentUser?.id &&
    isApril2026CampaignActive() &&
    isUserSignupApril2026(currentUser?.attributes?.createdAt) &&
    isRoboupLawnMowerListing(publicDataForPromo);
  const reolinkPromoConsumedEarly =
    currentUser?.attributes?.privateData?.promos?.reolinkWeekend2026Consumed === true;
  const isReolinkPromoEligibleAfterSignupEarly =
    shouldShowReolinkPromoPrice({
      currentUser,
      hasPromoQueryFlag: hasPromoReolinkFlagEarly,
      reolinkPromoConsumed: reolinkPromoConsumedEarly,
    }) && isReolinkSecurityCameraListing(publicDataForPromo);
  const showPromoPriceOnListing =
    isRoboupPromoEligibleAfterSignupEarly || isReolinkPromoEligibleAfterSignupEarly;

  const listingForOrderPanel = useMemo(() => {
    if (!showPromoPriceOnListing) return currentListing;
    const currentPrice = currentListing?.attributes?.price;
    const amount = currentPrice?.amount;
    if (!Number.isFinite(amount) || amount <= 0) return currentListing;

    const currency = currentPrice?.currency;
    if (!currency) return currentListing;
    const discountedAmount = Math.max(0, Math.round(amount * 0.95));
    if (discountedAmount === amount) return currentListing;

    return {
      ...currentListing,
      attributes: {
        ...currentListing.attributes,
        price: new Money(discountedAmount, currency),
      },
    };
  }, [currentListing, showPromoPriceOnListing]);

  const campaignPromoStrikeThroughPrice = useMemo(() => {
    if (!showPromoPriceOnListing) return null;
    const p = currentListing?.attributes?.price;
    const amount = p?.amount;
    if (!Number.isFinite(amount) || amount <= 0) return null;
    const discountedAmount = Math.max(0, Math.round(amount * 0.95));
    if (discountedAmount === amount) return null;
    return p;
  }, [showPromoPriceOnListing, currentListing]);

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  // 404 errors are terminal: show NotFound regardless of any in-flight retry
  if (showListingError && showListingError.status === 404) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  // Prefer the loading spinner over a red error flash whenever a fetch is in
  // flight or the cached listing is not yet fully hydrated (e.g. partial data
  // coming from a previous search/similar-listings query).
  const hasFullListingData =
    currentListing.id &&
    currentListing.attributes &&
    currentListing.attributes.publicData &&
    currentListing.attributes.publicData.listingType &&
    currentListing.attributes.publicData.transactionProcessAlias &&
    currentListing.attributes.publicData.unitType;

  if (showListingInProgress && !hasFullListingData) {
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  if (showListingError) {
    // Other error in fetching listing (fetch is no longer in progress)
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // No cached listing and no fetch in flight yet (e.g. before loadData runs)
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  // Check if listing attributes are fully loaded before validating publicData
  if (!currentListing.attributes || !currentListing.attributes.publicData) {
    // Still loading listing attributes
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;
  const hasPromoSignupFlag = new URLSearchParams(location.search || '').get('promoRoboup') === '1';
  const hasPromoReolinkFlag =
    new URLSearchParams(location.search || '').get('promoReolink') === '1';
  const isRoboupPromoEligibleAfterSignup =
    hasPromoSignupFlag &&
    !!currentUser?.id &&
    isApril2026CampaignActive() &&
    isUserSignupApril2026(currentUser?.attributes?.createdAt) &&
    isRoboupLawnMowerListing(publicData);

  const reolinkPromoConsumed =
    currentUser?.attributes?.privateData?.promos?.reolinkWeekend2026Consumed === true;
  const isReolinkPromoEligibleAfterSignup =
    shouldShowReolinkPromoPrice({
      currentUser,
      hasPromoQueryFlag: hasPromoReolinkFlag,
      reolinkPromoConsumed,
    }) && isReolinkSecurityCameraListing(publicData);

  // Extract compatibility data
  const compatibility = publicData.compatibility;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const { listingType, transactionProcessAlias, unitType } = publicData;
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType.
    // If we are still fetching the full listing, show the spinner to avoid a red
    // error flash while partial/cached data (e.g. from SearchPage) is displayed.
    if (showListingInProgress) {
      return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
    }
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking ? 'booking' : isPurchase ? 'purchase' : 'inquiry';

  const validListingTypes = listingConfig.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected;
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <div>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </div>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  const onSubmitCheckout = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const onSubmitAddToCart = values => {
    const orderData = values || {};
    const applyPromoPromise = isRoboupPromoEligibleAfterSignup
      ? props.onSetCartPromoCode(ROBOUP_PROMO_CODE)
      : isReolinkPromoEligibleAfterSignup
      ? props.onSetCartPromoCode(REOLINK_PROMO_CODE)
      : Promise.resolve();
    return applyPromoPromise
      .then(() => props.onAddToCart(currentListing, orderData))
      .then(() => {
        // Show success toast instead of immediate redirect
        setCartPanelOpen(true);
      })
      .catch(() => {});
  };

  const openSellerPanel = () => {
    setSellerPanelOpen(true);
  };

  const closeSellerPanel = () => {
    setSellerPanelOpen(false);
  };

  const openLoyaltyPanel = () => {
    setLoyaltyPanelOpen(true);
  };

  const closeLoyaltyPanel = () => {
    setLoyaltyPanelOpen(false);
  };

  const bundles = props.bundles || [];
  const bundleProductListingsById = props.bundleProductListingsById || {};
  const handleAddBundleToCart = async bundle => {
    if (!bundle || !Array.isArray(bundle.products) || bundle.products.length === 0) return;
    setAddingBundleId(bundle.id);
    try {
      for (const product of bundle.products) {
        const productListing = bundleProductListingsById[product.id];
        if (!productListing) continue;
        const orderData = buildCartOrderDataForListing(
          productListing,
          Number(product.quantity) > 0 ? Number(product.quantity) : 1
        );
        // eslint-disable-next-line no-await-in-loop
        await props.onAddToCart(productListing, orderData);
      }
      const bundlePromoCode = bundle?.price?.promo_code;
      if (bundlePromoCode && props.onAddCartPromoCode) {
        await props.onAddCartPromoCode(bundlePromoCode);
        await props.onAddBundleCartPromoCode(bundlePromoCode);
      }
      setCartPanelOpen(true);
    } catch (e) {
      // noop
    } finally {
      setAddingBundleId(null);
    }
  };

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      // This handler is used for inquiry (without payment) to submit directly
      // For purchase/booking, the CTA opens a modal and the FinalForm submission
      // will call the onSubmit passed to OrderPanel below
      onSubmitCheckout(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};

  const handleViewPhotosClick = e => {
    // Stop event from bubbling up to prevent image click handler
    // trying to open the carousel as well.
    e.stopPropagation();
    setImageCarouselOpen(true);
  };

  const actionBar =
    mounted && currentListing.id && isOwnListing ? (
      <>
        {noPayoutDetailsSetWithOwnListing ? (
          <ActionBarMaybe
            className={classNames(css.actionBarForHeroLayout, {
              [css.actionBarNoBorderRadiusOnMobile]: !showListingImage,
            })}
            isOwnListing={isOwnListing}
            listing={currentListing}
            showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
            currentUser={currentUser}
          />
        ) : null}
        <ActionBarMaybe
          className={classNames(css.actionBarForHeroLayout, {
            [css.actionBarNoBorderRadiusOnMobile]: !showListingImage,
          })}
          isOwnListing={isOwnListing}
          listing={currentListing}
          currentUser={currentUser}
          editParams={{
            id: listingId.uuid,
            slug: listingSlug,
            type: listingPathParamType,
            tab: listingTab,
          }}
        />
      </>
    ) : null;

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price),
          ...availabilityMaybe,
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        {showListingImage ? (
          <SectionHero
            title={title}
            listing={currentListing}
            isOwnListing={isOwnListing}
            imageCarouselOpen={imageCarouselOpen}
            onImageCarouselClose={() => setImageCarouselOpen(false)}
            handleViewPhotosClick={handleViewPhotosClick}
            onManageDisableScrolling={onManageDisableScrolling}
            actionBar={actionBar}
          />
        ) : (
          isOwnListing && <div className={css.actionBarContainerForNoListingImage}>{actionBar}</div>
        )}

        {/* Compatibility Badges Section */}
        <CompatibilityBadges compatibility={compatibility} config={config} />
        <div className={css.contentWrapperForHeroLayout}>
          <div className={css.mainColumnForHeroLayout}>
            <div className={showListingImage ? css.mobileHeading : css.noListingImageHeadingHero}>
              {showListingImage ? (
                // add css logic here that applies larger margin on mobile view to push down title
                <H4 as="h2" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
              ) : (
                <H3
                  as="h2"
                  className={classNames(css.orderPanelTitle, {
                    [css.titleMarginForOwnListingNoImage]: isOwnListing,
                  })}
                >
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H3>
              )}
            </div>
          </div>
          <div className={css.orderColumnForHeroLayout}>
            <OrderPanel
              className={css.orderPanel}
              listing={listingForOrderPanel}
              isOwnListing={isOwnListing}
              onSubmit={isPurchase ? onSubmitAddToCart : handleOrderSubmit}
              authorLink={
                <NamedLink
                  className={css.authorNameLink}
                  name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                  params={params}
                  to={{ hash: '#author' }}
                >
                  {authorDisplayName}
                </NamedLink>
              }
              title={<FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />}
              titleDesktop={
                <H4 as="h2" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
              }
              payoutDetailsWarning={payoutDetailsWarning}
              author={ensuredAuthor}
              onManageDisableScrolling={onManageDisableScrolling}
              onContactUser={onContactUser}
              onOpenSellerPanel={openSellerPanel}
              onOpenLoyaltyPanel={openLoyaltyPanel}
              {...restOfProps}
              validListingTypes={config.listing.listingTypes}
              marketplaceCurrency={config.currency}
              dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
              marketplaceName={config.marketplaceName}
              showListingImage={showListingImage}
              campaignPromoStrikeThroughPrice={campaignPromoStrikeThroughPrice}
              campaignPromoPercentOff={5}
            />
          </div>
        </div>

        {bundles && bundles.length > 0 ? (
          <div className={css.fullWidthDescriptionContainer}>
            <SectionBundles
              bundles={bundles}
              productListingsById={bundleProductListingsById}
              onAddBundleToCart={handleAddBundleToCart}
              intl={intl}
              config={config}
              addingBundleId={addingBundleId}
            />
          </div>
        ) : null}

        {/* Full-width description and details section */}
        <div className={css.fullWidthDescriptionContainer}>
          <SectionDescriptionAndDetails
            text={description}
            showAsIngress
            enableMarkdown
            publicData={publicData}
            metadata={metadata}
            listingFieldConfigs={listingConfig.listingFields}
            categoryConfiguration={config.categoryConfiguration}
            intl={intl}
            listing={currentListing}
            price={price}
            onOpenLoyaltyPanel={openLoyaltyPanel}
          />
        </div>

        {geolocation && (
          <div className={css.contentWrapperForHeroLayout}>
            <div className={css.mainColumnForHeroLayout}>
              <SectionMapMaybe
                geolocation={geolocation}
                publicData={publicData}
                listingId={currentListing.id}
                mapsConfig={config.maps}
              />
            </div>
            <div className={css.orderColumnForHeroLayout}>
              {/* Empty order column to maintain layout */}
            </div>
          </div>
        )}
      </LayoutSingleColumn>

      <SellerInfoPanel isOpen={sellerPanelOpen} onClose={closeSellerPanel} author={ensuredAuthor} />
      <LoyaltyProgramPanel isOpen={loyaltyPanelOpen} onClose={closeLoyaltyPanel} />

      <CartSidePanel open={cartPanelOpen} onClose={() => setCartPanelOpen(false)} />
    </Page>
  );
};

/**
 * The ListingPage component with cover photo layout
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Object} props.currentUser - The current user
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error

 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    showListingInProgress,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    bundles = [],
    bundleProductIds = [],
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const bundleProductRefs = (bundleProductIds || []).map(id => ({ id, type: 'listing' }));
  const bundleProductListings = getMarketplaceEntities(state, bundleProductRefs);
  const bundleProductListingsById = bundleProductListings.reduce((acc, listing) => {
    const id = listing?.id?.uuid || listing?.id;
    if (id) acc[id] = listing;
    return acc;
  }, {});

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    showListingInProgress,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots, // for OrderPanel
    timeSlotsForDate, // for OrderPanel
    lineItems, // for OrderPanel
    fetchLineItemsInProgress, // for OrderPanel
    fetchLineItemsError, // for OrderPanel
    sendInquiryInProgress,
    sendInquiryError,
    bundles,
    bundleProductListingsById,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)), // for OrderPanel
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)), // for OrderPanel
  onAddToCart: (listing, orderData) => dispatch(addToCart({ listing, orderData })),
  onSetCartPromoCode: code => dispatch(setCartPromoCode(code)),
  onAddCartPromoCode: code => dispatch(addCartPromoCode(code)),
  onAddBundleCartPromoCode: code => dispatch(addBundleCartPromoCode(code)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedListingPage);

export default ListingPage;
